import React, { useState, useCallback } from 'react';
import { Handle, Position } from 'reactflow';
import { Col } from "reactstrap";
import ReactFlow, {
    Controls,
    Background,
    useNodesState,
    useEdgesState,
    MarkerType,
    ConnectionMode
} from 'reactflow';
import { Copy, Plus, Trash2, RotateCcw, Target, ArrowUpDown, ArrowRight } from 'lucide-react';
import { useDarkMode } from 'context/DarkModeProvider';
import { useLanguage } from 'context/LanguageProvider';
import 'reactflow/dist/style.css';
import "../../assets/css/CreateYourOwn/CommonCreate.css";
import CodeHighlighter from 'components/CodeHighlighter';

// ─── Custom Node ──────────────────────────────────────────────────────────────
const CustomNode = ({ id, data, selected }) => {
    const nodeStyle = {
        padding: '12px 16px',
        borderRadius: '30px',
        border: selected ? '3px solid #506ce9ff' : '2px solid #667eea',
        background: selected
            ? 'linear-gradient(135deg, #506ce9ff, #334597ff)'
            : 'linear-gradient(135deg, #667eea, #764ba2)',
        color: 'white',
        fontSize: '14px',
        fontWeight: '600',
        minWidth: '60px',
        textAlign: 'center',
        boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
        transition: 'all 0.2s ease',
        cursor: 'pointer',
        position: 'relative'
    };

    return (
        <div style={nodeStyle}>
            <Handle type="target" position={Position.Top}    style={{ background: 'transparent', border: 'none', width: 8, height: 8, opacity: 0 }} />
            <Handle type="target" position={Position.Left}   style={{ background: 'transparent', border: 'none', width: 8, height: 8, opacity: 0 }} />
            <Handle type="target" position={Position.Right}  style={{ background: 'transparent', border: 'none', width: 8, height: 8, opacity: 0 }} />
            {data.label}
            <Handle type="source" position={Position.Bottom} style={{ background: 'transparent', border: 'none', width: 8, height: 8, opacity: 0 }} />
            <Handle type="source" position={Position.Left}   style={{ background: 'transparent', border: 'none', width: 8, height: 8, opacity: 0 }} />
            <Handle type="source" position={Position.Right}  style={{ background: 'transparent', border: 'none', width: 8, height: 8, opacity: 0 }} />
        </div>
    );
};

const nodeTypes = { custom: CustomNode };

// ─── Main Component ───────────────────────────────────────────────────────────
const GraphBuilder = () => {
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const [nodeCounter, setNodeCounter]   = useState(1);
    const [nodeValue, setNodeValue]       = useState('');
    const [mode, setMode]                 = useState('add');
    const [connectingFrom, setConnectingFrom] = useState(null);
    const [selectedLanguage, setSelectedLanguage] = useState('javascript');
    const [copied, setCopied]             = useState(false);
    const [showInfo, setShowInfo]         = useState(false);
    const [isDirected, setIsDirected]     = useState(true);
    const [actionLog, setActionLog]       = useState([]);

    const { darkMode }    = useDarkMode();
    const { selLanguage } = useLanguage();

    // ── Node click ────────────────────────────────────────────────────────────
    const onNodeClick = useCallback((event, node) => {
        event.stopPropagation();

        if (mode === 'delete') {
            setNodes(nds => nds.filter(n => n.id !== node.id));
            setEdges(eds => eds.filter(e => e.source !== node.id && e.target !== node.id));
            if (connectingFrom === node.id) setConnectingFrom(null);
            setActionLog(prev => [...prev, { type: 'deleteNode', value: node.data.label }]);
        } else if (mode === 'connect') {
            if (!connectingFrom) {
                setConnectingFrom(node.id);
            } else if (connectingFrom === node.id) {
                setConnectingFrom(null);
            } else {
                const edgeExists = edges.some(e =>
                    (e.source === connectingFrom && e.target === node.id) ||
                    (!isDirected && e.source === node.id && e.target === connectingFrom)
                );
                if (!edgeExists) {
                    const fromNode = nodes.find(n => n.id === connectingFrom);
                    const newEdge = {
                        id: `e${connectingFrom}-${node.id}`,
                        source: connectingFrom,
                        target: node.id,
                        type: 'default',
                        markerEnd: isDirected ? { type: MarkerType.ArrowClosed } : undefined,
                        style: { strokeWidth: 2, stroke: '#667eea' },
                    };
                    setEdges(eds => [...eds, newEdge]);
                    setActionLog(prev => [...prev, {
                        type: 'addEdge',
                        from: fromNode?.data.label,
                        to: node.data.label
                    }]);
                }
                setConnectingFrom(null);
            }
        }
    }, [mode, connectingFrom, isDirected, edges, nodes, setNodes, setEdges]);

    // ── Edge click ────────────────────────────────────────────────────────────
    const onEdgeClick = useCallback((event, edge) => {
        if (mode === 'delete') {
            setEdges(eds => eds.filter(e => e.id !== edge.id));
            const fromNode = nodes.find(n => n.id === edge.source);
            const toNode   = nodes.find(n => n.id === edge.target);
            setActionLog(prev => [...prev, {
                type: 'deleteEdge',
                from: fromNode?.data.label,
                to: toNode?.data.label
            }]);
        }
    }, [mode, setEdges, nodes]);

    // ── Add node ──────────────────────────────────────────────────────────────
    const addNode = useCallback(() => {
        const value = nodeValue.trim() || nodeCounter.toString();
        const newNode = {
            id: `node_${nodeCounter}`,
            type: 'custom',
            position: {
                x: 100 + (nodeCounter * 80) % 500,
                y: 100 + (Math.floor(nodeCounter / 6) * 150)
            },
            data: { label: value },
        };
        setNodes(nds => [...nds, newNode]);
        setNodeCounter(nodeCounter + 1);
        setNodeValue('');
        setActionLog(prev => [...prev, { type: 'addNode', value }]);
    }, [nodeValue, nodeCounter, setNodes]);

    // ── Clear all ─────────────────────────────────────────────────────────────
    const clearAll = useCallback(() => {
        setNodes([]);
        setEdges([]);
        setNodeCounter(1);
        setConnectingFrom(null);
        setNodeValue('');
        setActionLog(prev => [...prev, { type: 'clear' }]);
    }, [setNodes, setEdges]);

    // ── Toggle directed ───────────────────────────────────────────────────────
    const toggleDirected = useCallback(() => {
        if (edges.length > 0) {
            alert('Δεν μπορείτε να αλλάξετε τον τύπο γραφήματος όταν υπάρχουν ακμές. Πρώτα καθαρίστε όλες τις ακμές.');
            return;
        }
        setIsDirected(prev => !prev);
    }, [edges.length]);

    // ── Code generation (actionLog) ───────────────────────────────────────────
    const generateCode = useCallback(() => {
        const graphType = isDirected ? 'Directed Graph' : 'Undirected Graph';
        const directedBool = { js: isDirected, py: isDirected ? 'True' : 'False', cpp: isDirected ? 'true' : 'false', java: isDirected };

        const renderActions = (lang) => {
            return actionLog.map(action => {
                if (action.type === 'addNode') {
                    if (lang === 'javascript') return `    myGraph.addVertex("${action.value}");`;
                    if (lang === 'python')     return `    my_graph.add_vertex("${action.value}")`;
                    if (lang === 'java')       return `        myGraph.addVertex("${action.value}");`;
                    if (lang === 'cpp')        return `    myGraph.addVertex("${action.value}");`;
                }
                if (action.type === 'addEdge') {
                    if (lang === 'javascript') return `    myGraph.addEdge("${action.from}", "${action.to}");`;
                    if (lang === 'python')     return `    my_graph.add_edge("${action.from}", "${action.to}")`;
                    if (lang === 'java')       return `        myGraph.addEdge("${action.from}", "${action.to}");`;
                    if (lang === 'cpp')        return `    myGraph.addEdge("${action.from}", "${action.to}");`;
                }
                if (action.type === 'deleteNode') {
                    if (lang === 'javascript') return `    myGraph.removeVertex("${action.value}");`;
                    if (lang === 'python')     return `    my_graph.remove_vertex("${action.value}")`;
                    if (lang === 'java')       return `        myGraph.removeVertex("${action.value}");`;
                    if (lang === 'cpp')        return `    myGraph.removeVertex("${action.value}");`;
                }
                if (action.type === 'deleteEdge') {
                    if (lang === 'javascript') return `    myGraph.removeEdge("${action.from}", "${action.to}");`;
                    if (lang === 'python')     return `    my_graph.remove_edge("${action.from}", "${action.to}")`;
                    if (lang === 'java')       return `        myGraph.removeEdge("${action.from}", "${action.to}");`;
                    if (lang === 'cpp')        return `    myGraph.removeEdge("${action.from}", "${action.to}");`;
                }
                if (action.type === 'clear') {
                    if (lang === 'javascript') return `    // Graph was cleared at this point`;
                    if (lang === 'python')     return `    # Graph was cleared at this point`;
                    if (lang === 'java')       return `        // Graph was cleared at this point`;
                    if (lang === 'cpp')        return `    // Graph was cleared at this point`;
                }
                return '';
            }).filter(Boolean).join('\n');
        };

        const codes = {
            javascript: `// ${graphType} Implementation in JavaScript

class Graph {
    constructor(directed = ${directedBool.js}) {
        this.directed = directed;
        this.adjacencyList = new Map();
    }

    addVertex(vertex) {
        if (!this.adjacencyList.has(vertex)) {
            this.adjacencyList.set(vertex, []);
        }
    }

    addEdge(vertex1, vertex2) {
        this.addVertex(vertex1);
        this.addVertex(vertex2);
        this.adjacencyList.get(vertex1).push(vertex2);
        if (!this.directed) {
            this.adjacencyList.get(vertex2).push(vertex1);
        }
    }

    removeVertex(vertex) {
        if (!this.adjacencyList.has(vertex)) return;
        this.adjacencyList.delete(vertex);
        for (let [key, neighbors] of this.adjacencyList) {
            this.adjacencyList.set(key, neighbors.filter(n => n !== vertex));
        }
    }

    removeEdge(vertex1, vertex2) {
        if (this.adjacencyList.has(vertex1)) {
            this.adjacencyList.set(vertex1, this.adjacencyList.get(vertex1).filter(n => n !== vertex2));
        }
        if (!this.directed && this.adjacencyList.has(vertex2)) {
            this.adjacencyList.set(vertex2, this.adjacencyList.get(vertex2).filter(n => n !== vertex1));
        }
    }

    getNeighbors(vertex) {
        return this.adjacencyList.get(vertex) || [];
    }

    hasEdge(vertex1, vertex2) {
        return this.adjacencyList.get(vertex1)?.includes(vertex2) || false;
    }

    display() {
        for (let [vertex, edges] of this.adjacencyList) {
            console.log(\`\${vertex} -> \${edges.join(', ')}\`);
        }
    }
}

// Main
function main() {
    const myGraph = new Graph(${directedBool.js});
${renderActions('javascript')}
    myGraph.display();
}

main();`,

            python: `# ${graphType} Implementation in Python

class Graph:
    def __init__(self, directed=${directedBool.py}):
        self.directed = directed
        self.adjacency_list = {}

    def add_vertex(self, vertex):
        if vertex not in self.adjacency_list:
            self.adjacency_list[vertex] = []

    def add_edge(self, vertex1, vertex2):
        self.add_vertex(vertex1)
        self.add_vertex(vertex2)
        self.adjacency_list[vertex1].append(vertex2)
        if not self.directed:
            self.adjacency_list[vertex2].append(vertex1)

    def remove_vertex(self, vertex):
        if vertex not in self.adjacency_list:
            return
        del self.adjacency_list[vertex]
        for key in self.adjacency_list:
            self.adjacency_list[key] = [n for n in self.adjacency_list[key] if n != vertex]

    def remove_edge(self, vertex1, vertex2):
        if vertex1 in self.adjacency_list:
            self.adjacency_list[vertex1] = [n for n in self.adjacency_list[vertex1] if n != vertex2]
        if not self.directed and vertex2 in self.adjacency_list:
            self.adjacency_list[vertex2] = [n for n in self.adjacency_list[vertex2] if n != vertex1]

    def get_neighbors(self, vertex):
        return self.adjacency_list.get(vertex, [])

    def has_edge(self, vertex1, vertex2):
        return vertex2 in self.adjacency_list.get(vertex1, [])

    def display(self):
        for vertex, edges in self.adjacency_list.items():
            print(f"{vertex} -> {', '.join(map(str, edges))}")

# Main
if __name__ == "__main__":
    my_graph = Graph(directed=${directedBool.py})
${renderActions('python')}
    my_graph.display()`,

            java: `// ${graphType} Implementation in Java
import java.util.*;

class Graph {
    private boolean directed;
    private Map<String, List<String>> adjacencyList;

    public Graph(boolean directed) {
        this.directed = directed;
        this.adjacencyList = new HashMap<>();
    }

    public void addVertex(String vertex) {
        adjacencyList.putIfAbsent(vertex, new ArrayList<>());
    }

    public void addEdge(String vertex1, String vertex2) {
        addVertex(vertex1);
        addVertex(vertex2);
        adjacencyList.get(vertex1).add(vertex2);
        if (!directed) {
            adjacencyList.get(vertex2).add(vertex1);
        }
    }

    public void removeVertex(String vertex) {
        adjacencyList.remove(vertex);
        for (List<String> neighbors : adjacencyList.values()) {
            neighbors.remove(vertex);
        }
    }

    public void removeEdge(String vertex1, String vertex2) {
        if (adjacencyList.containsKey(vertex1)) {
            adjacencyList.get(vertex1).remove(vertex2);
        }
        if (!directed && adjacencyList.containsKey(vertex2)) {
            adjacencyList.get(vertex2).remove(vertex1);
        }
    }

    public List<String> getNeighbors(String vertex) {
        return adjacencyList.getOrDefault(vertex, new ArrayList<>());
    }

    public boolean hasEdge(String vertex1, String vertex2) {
        return adjacencyList.getOrDefault(vertex1, new ArrayList<>()).contains(vertex2);
    }

    public void display() {
        for (Map.Entry<String, List<String>> entry : adjacencyList.entrySet()) {
            System.out.println(entry.getKey() + " -> " + String.join(", ", entry.getValue()));
        }
    }
}

public class Main {
    public static void main(String[] args) {
        Graph myGraph = new Graph(${directedBool.java});
${renderActions('java')}
        myGraph.display();
    }
}`,

            cpp: `// ${graphType} Implementation in C++
#include <iostream>
#include <vector>
#include <string>
#include <unordered_map>
#include <algorithm>
using namespace std;

class Graph {
private:
    bool directed;
    unordered_map<string, vector<string>> adjacencyList;

public:
    Graph(bool isDirected = ${directedBool.cpp}) : directed(isDirected) {}

    void addVertex(const string& vertex) {
        if (adjacencyList.find(vertex) == adjacencyList.end()) {
            adjacencyList[vertex] = {};
        }
    }

    void addEdge(const string& vertex1, const string& vertex2) {
        addVertex(vertex1);
        addVertex(vertex2);
        adjacencyList[vertex1].push_back(vertex2);
        if (!directed) {
            adjacencyList[vertex2].push_back(vertex1);
        }
    }

    void removeVertex(const string& vertex) {
        adjacencyList.erase(vertex);
        for (auto& pair : adjacencyList) {
            auto& v = pair.second;
            v.erase(remove(v.begin(), v.end(), vertex), v.end());
        }
    }

    void removeEdge(const string& vertex1, const string& vertex2) {
        if (adjacencyList.count(vertex1)) {
            auto& v = adjacencyList[vertex1];
            v.erase(remove(v.begin(), v.end(), vertex2), v.end());
        }
        if (!directed && adjacencyList.count(vertex2)) {
            auto& v = adjacencyList[vertex2];
            v.erase(remove(v.begin(), v.end(), vertex1), v.end());
        }
    }

    vector<string> getNeighbors(const string& vertex) {
        auto it = adjacencyList.find(vertex);
        return it != adjacencyList.end() ? it->second : vector<string>{};
    }

    bool hasEdge(const string& vertex1, const string& vertex2) {
        auto it = adjacencyList.find(vertex1);
        if (it != adjacencyList.end()) {
            return find(it->second.begin(), it->second.end(), vertex2) != it->second.end();
        }
        return false;
    }

    void display() {
        for (const auto& pair : adjacencyList) {
            cout << pair.first << " -> ";
            for (const auto& neighbor : pair.second) cout << neighbor << " ";
            cout << endl;
        }
    }
};

int main() {
    Graph myGraph(${directedBool.cpp});
${renderActions('cpp')}
    myGraph.display();
    return 0;
}`
        };

        return codes[selectedLanguage] || '';
    }, [actionLog, selectedLanguage, isDirected]);

    // ── Copy to clipboard ─────────────────────────────────────────────────────
    const copyToClipboard = useCallback(() => {
        navigator.clipboard.writeText(generateCode()).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    }, [generateCode]);

    // ── Styles ────────────────────────────────────────────────────────────────
    const buttonBase = {
        padding: '16px 24px',
        borderRadius: '16px',
        fontSize: '1rem',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        fontFamily: 'Poppins, sans-serif',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        background: darkMode ? '#1e293b' : 'var(--customBuildButton)',
        color: '#7a8effff',
        border: '2px solid #7a8effff',
    };

    const activeButton = {
        ...buttonBase,
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        border: 'none',
    };

    const hoverProps = {
        onMouseEnter: (e) => { e.currentTarget.style.transform = 'scale(1.05)'; },
        onMouseLeave: (e) => { e.currentTarget.style.transform = 'scale(1)'; },
    };

    return (
        <>
            <style>{`
                .gb-container {
                    display: flex;
                    min-height: 100vh;
                    background: ${darkMode ? '#0f172a' : 'var(--lightModeBack)'};
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                    padding: 20px;
                    margin-bottom: 20%;
                }
                .gb-col {
                    background: ${darkMode ? '#1E293B' : 'var(--customBuildBack)'};
                    backdrop-filter: blur(20px);
                    border-radius: 24px;
                    padding: 30px;
                    box-shadow: 0 25px 50px rgba(0,0,0,0.15);
                    border: 1px solid ${darkMode ? 'rgba(122,142,255,0.3)' : 'rgba(0,0,0,0.2)'};
                    width: 100%;
                }
                .gb-title {
                    font-size: 2.5rem;
                    font-weight: 800;
                    background: rgb(122,142,255);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                    text-align: center;
                    margin-bottom: 30px;
                    letter-spacing: -0.02em;
                }
                .gb-label {
                    font-size: 0.875rem;
                    font-weight: 600;
                    color: ${darkMode ? '#fff' : '#0b0a0a'};
                    margin-bottom: 8px;
                }
                .gb-input {
                    width: 45%;
                    padding: 12px 16px;
                    border: 2px solid #e5e7eb;
                    border-radius: 8px;
                    font-size: 0.9rem;
                    outline: none;
                }
                .gb-input::placeholder { color: #363a42ff; opacity: 1; }
                .gb-select {
                    width: 100%;
                    padding: 12px 16px;
                    border: 2px solid #e5e7eb;
                    border-radius: 8px;
                    font-size: 0.9rem;
                    outline: none;
                    margin-bottom: 24px;
                }
                .gb-hint-btn {
                    padding: 16px 20px;
                    border-radius: 16px;
                    border: 2px solid #7a8effff;
                    font-size: 1rem;
                    font-weight: 600;
                    cursor: pointer;
                    background: ${darkMode ? '#1e293b' : 'var(--customBuildButton)'};
                    color: #7a8effff;
                    min-width: 56px;
                    height: 56px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                }
                .gb-flow {
                    height: 500px;
                    width: 100%;
                    border: ${!darkMode ? '1px solid #0e0f11ff' : 'none'};
                    border-radius: 12px;
                    margin-bottom: 30px;
                }
                .gb-tooltip {
                    position: absolute;
                    top: 100%;
                    left: 50%;
                    transform: translateX(-50%);
                    margin-top: 10px;
                    padding: 20px;
                    background: linear-gradient(135deg, rgba(30,41,59,0.98), rgba(51,65,85,0.98));
                    backdrop-filter: blur(20px);
                    border-radius: 16px;
                    border: 1px solid rgba(122,142,255,0.3);
                    box-shadow: 0 20px 40px rgba(0,0,0,0.3);
                    z-index: 1000;
                    min-width: 280px;
                }
                .gb-copy-btn {
                    width: 100%;
                    margin-top: 20px;
                    padding: 16px 24px;
                    border-radius: 16px;
                    border: none;
                    font-size: 1rem;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    color: white;
                }
                .gb-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 15px;
                    margin-bottom: 30px;
                }
                @media (max-width: 1024px) {
                    .gb-grid { grid-template-columns: repeat(2, 1fr); }
                    .gb-flow { height: 400px; }
                }
                @media (max-width: 768px) {
                    .gb-container { padding: 10px; }
                    .gb-col { padding: 15px; border-radius: 16px; }
                    .gb-title { font-size: 1.5rem; }
                    .gb-grid { grid-template-columns: 1fr; }
                    .gb-flow { height: 300px; }
                    .gb-input { width: 100%; }
                }
                @media (max-width: 480px) {
                    .gb-title { font-size: 1.25rem; }
                    .gb-flow { height: 250px; }
                }
            `}</style>

            <div className="gb-container">
                <Col className="gb-col">
                    <h1 className="gb-title">
                        {selLanguage ? "Δημιουργία Γράφου" : "Graph Builder"}
                    </h1>

                    {/* Graph Type Toggle */}
                    <div style={{ marginBottom: '24px' }}>
                        <div className="gb-label">
                            {selLanguage ? "Τύπος Γράφου" : "Graph Type"}
                        </div>
                        <button
                            onClick={toggleDirected}
                            {...hoverProps}
                            style={{
                                ...buttonBase,
                                background: edges.length > 0
                                    ? '#64748b'
                                    : isDirected
                                        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                        : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                color: 'white',
                                border: 'none',
                                cursor: edges.length > 0 ? 'not-allowed' : 'pointer',
                                opacity: edges.length > 0 ? 0.6 : 1,
                            }}
                            disabled={edges.length > 0}
                        >
                            {isDirected ? <ArrowRight size={16} /> : <ArrowUpDown size={16} />}
                            {isDirected
                                ? (selLanguage ? 'Κατευθυνόμενο' : 'Directed')
                                : (selLanguage ? 'Μη Κατευθυνόμενο' : 'Undirected')}
                        </button>
                    </div>

                    {/* Input + Hint */}
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', marginBottom: '30px' }}>
                        <input
                            type="text"
                            value={nodeValue}
                            onChange={(e) => setNodeValue(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && addNode()}
                            placeholder={selLanguage ? "Εισάγετε τιμή κόμβου..." : "Enter node value..."}
                            className="gb-input"
                        />

                        <div style={{ position: 'relative' }}>
                            <button
                                className="gb-hint-btn"
                                onMouseOver={() => setShowInfo(true)}
                                onMouseOut={() => setShowInfo(false)}
                            >💡</button>
                            {showInfo && (
                                <div
                                    className="gb-tooltip"
                                    onMouseEnter={() => setShowInfo(true)}
                                    onMouseLeave={() => setShowInfo(false)}
                                >
                                    <div style={{
                                        position: 'absolute', top: '-8px', left: '50%',
                                        transform: 'translateX(-50%)',
                                        borderLeft: '8px solid transparent', borderRight: '8px solid transparent',
                                        borderBottom: '8px solid rgba(30,41,59,0.98)',
                                        width: 0, height: 0,
                                    }} />
                                    <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#fff', marginBottom: '15px', textAlign: 'center' }}>
                                        {selLanguage ? "Οδηγίες Χρήσης" : "Instructions"}
                                    </h3>
                                    <div style={{ fontSize: '0.85rem', color: '#cbd5e1', lineHeight: '1.6' }}>
                                        <div><strong>{selLanguage ? "Προσθήκη:" : "Add:"}</strong> {selLanguage ? "Κλικ 'Προσθήκη Κόμβου'" : "Click 'Add Node'"}</div>
                                        <div><strong>{selLanguage ? "Σύνδεση:" : "Connect:"}</strong> {selLanguage ? "Επιλογή 'Σύνδεση' → κλικ σε 2 κόμβους" : "Select 'Connect' → click 2 nodes"}</div>
                                        <div><strong>{selLanguage ? "Διαγραφή:" : "Delete:"}</strong> {selLanguage ? "Επιλογή 'Διαγραφή' → κλικ στοιχείο" : "Select 'Delete' → click element"}</div>
                                        <div><strong>{selLanguage ? "Μετακίνηση:" : "Move:"}</strong> {selLanguage ? "Drag & drop κόμβους" : "Drag & drop nodes"}</div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Operation Buttons */}
                    <div className="gb-label">{selLanguage ? "Λειτουργίες" : "Functions"}</div>
                    <div className="gb-grid">
                        <button onClick={addNode} style={buttonBase} {...hoverProps}>
                            <Plus size={16} />
                            <span>{selLanguage ? "Προσθήκη Κόμβου" : "Add Node"}</span>
                        </button>

                        <button
                            onClick={() => { setMode('connect'); setConnectingFrom(null); }}
                            style={mode === 'connect' ? activeButton : buttonBase}
                            {...hoverProps}
                        >
                            <Target size={16} />
                            <span>{selLanguage ? "Σύνδεση" : "Connect"} {connectingFrom ? '(1/2)' : ''}</span>
                        </button>

                        <button
                            onClick={() => { setMode('delete'); setConnectingFrom(null); }}
                            style={mode === 'delete' ? activeButton : buttonBase}
                            {...hoverProps}
                        >
                            <Trash2 size={16} />
                            <span>{selLanguage ? "Διαγραφή" : "Delete"}</span>
                        </button>

                        <button onClick={clearAll} style={buttonBase} {...hoverProps}>
                            <RotateCcw size={16} />
                            <span>{selLanguage ? "Καθαρισμός" : "Restart"}</span>
                        </button>
                    </div>

                    {/* ReactFlow Canvas */}
                    <div className="gb-flow">
                        <ReactFlow
                            nodes={nodes}
                            edges={edges}
                            onNodesChange={onNodesChange}
                            onEdgesChange={onEdgesChange}
                            onNodeClick={onNodeClick}
                            onEdgeClick={onEdgeClick}
                            nodeTypes={nodeTypes}
                            connectionMode={ConnectionMode.Loose}
                            fitView
                            defaultEdgeOptions={{
                                type: 'default',
                                markerEnd: isDirected ? { type: MarkerType.ArrowClosed } : undefined,
                                style: { strokeWidth: 2, stroke: '#667eea' }
                            }}
                            style={{
                                background: 'linear-gradient(135deg, #f0f9ff 0%, #e0e7ff 100%)',
                                height: '100%',
                                borderRadius: '12px'
                            }}
                        >
                            <Controls />
                            <Background variant="dots" gap={20} size={1} color="#cbd5e1" />
                        </ReactFlow>
                    </div>

                    {/* Language Selector */}
                    <div className="gb-label">{selLanguage ? "Γλώσσα Προγραμματισμού" : "Programming Language"}</div>
                    <select
                        value={selectedLanguage}
                        onChange={(e) => setSelectedLanguage(e.target.value)}
                        className="gb-select"
                    >
                        <option value="javascript">JavaScript</option>
                        <option value="python">Python</option>
                        <option value="cpp">C++</option>
                        <option value="java">Java</option>
                    </select>

                    {/* Generated Code */}
                    <div className="gb-label">{selLanguage ? "Παραγόμενος Κώδικας" : "Generated Code"}</div>
                    <div style={{
                        background: 'linear-gradient(145deg, #0f172a, #1e293b)',
                        borderRadius: '8px',
                        border: '1px solid #334155',
                        overflow: 'auto',
                        maxHeight: '300px',
                        fontSize: '0.75rem',
                    }}>
                        <CodeHighlighter code={generateCode()} language={selectedLanguage} />
                    </div>

                    <button
                        className="gb-copy-btn"
                        onClick={copyToClipboard}
                        style={{
                            background: copied
                                ? 'linear-gradient(135deg, #059669 0%, #047857 100%)'
                                : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            transform: copied ? 'translateY(-4px)' : 'translateY(0)',
                        }}
                        onMouseOver={(e) => !copied && (e.currentTarget.style.transform = 'translateY(-2px)')}
                        onMouseOut={(e) => !copied && (e.currentTarget.style.transform = 'translateY(0)')}
                    >
                        <Copy size={16} />
                        <span>{copied ? (selLanguage ? 'Αντιγράφηκε!' : 'Copied') : (selLanguage ? 'Αντιγραφή' : 'Copy')}</span>
                    </button>
                </Col>
            </div>
        </>
    );
};

export default GraphBuilder;